import { Injectable } from '@angular/core';
import { OrdersService } from './orders.service';
import { AlertService } from '../alert/alert.service';
import { Orders } from '../../interfaces/orders';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OrdersExportService {
  private destroy$ = new Subject<void>();

  constructor(
    private ordersService: OrdersService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {
  }

  async showExportDialog(
    accountType: string,
    typeOrders: string,
    searchParams: any,
    userRole?: string
  ): Promise<void> {
    const message = await firstValueFrom(this.translate.get("swal_messages.export.text"));
    const title = await firstValueFrom(this.translate.get("swal_messages.export.title"));
    const title_selector = await firstValueFrom(this.translate.get("swal_messages.export.title_selector"));
    const all = await firstValueFrom(this.translate.get("swal_messages.export.all"));
    const billing = await firstValueFrom(this.translate.get("swal_messages.export.billing"));
    const delivery = await firstValueFrom(this.translate.get("swal_messages.export.delivery"));
    const custom = await firstValueFrom(this.translate.get("swal_messages.export.custom"));

    const isSupplierAdmin = accountType?.toLowerCase() === 'supplier' && userRole?.toLowerCase() === 'admin';
    const isBrokerAdmin = accountType?.toLowerCase() === 'broker' && userRole?.toLowerCase() === 'admin';

    let html = typeOrders == 'telemarketing-pending'
      ? `<label for="formatSelector" style="display: block; margin-bottom: 8px;">${title_selector}</label>
         <select id="formatSelector" class="swal2-input" style="width: 100%; border-radius: 8px;">
           <option value="all" selected>${all}</option>
         </select>`
      : accountType == 'supplier'
        ? `<label for="formatSelector" style="display: block; margin-bottom: 8px;">${title_selector}</label>
           <select id="formatSelector" class="swal2-input" style="width: 100%; border-radius: 8px;">
             <option value="all" selected>${all}</option>
             <option value="billing">${billing}</option>
             <option value="delivery">${delivery}</option>
             ${isSupplierAdmin ? `<option value="custom">${custom}</option>` : ''}
           </select>`
        : `<label for="formatSelector" style="display: block; margin-bottom: 8px;">${title_selector}</label>
           <select id="formatSelector" class="swal2-input" style="width: 100%; border-radius: 8px;">
             <option value="all" selected>${all}</option>
             ${isBrokerAdmin ? `<option value="custom">${custom}</option>` : ''}
           </select>`;

    const result = await Swal.fire({
      title: title,
      text: message,
      icon: 'info',
      html: html,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Excel (.xlsx)',
      denyButtonText: 'CSV',
      cancelButtonText: 'XLS',
      preConfirm: () => {
        const selector = document.getElementById('formatSelector') as HTMLSelectElement;
        return selector?.value;
      },
      preDeny: () => {
        const selector = document.getElementById('formatSelector') as HTMLSelectElement;
        return selector?.value;
      }
    });

    const option = result.value;

    if (result.isConfirmed) {
      if (option === 'custom') {
        this.showColumnSelector('xlsx', searchParams, typeOrders, userRole);
      } else {
        this.exportOrders('xlsx', option, searchParams, typeOrders, userRole);
      }
    } else if (result.isDenied) {
      const denyOption = result.value;
      if (denyOption === 'custom') {
        this.showColumnSelector('csv', searchParams, typeOrders, userRole);
      } else {
        this.exportOrders('csv', denyOption, searchParams, typeOrders, userRole);
      }
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      const selector = document.getElementById('formatSelector') as HTMLSelectElement;
      const cancelOption = selector?.value || 'all';
      if (cancelOption === 'custom') {
        this.showColumnSelector('xls', searchParams, typeOrders, userRole);
      } else {
        this.exportOrders('xls', cancelOption, searchParams, typeOrders, userRole);
      }
    }
  }

  private async showColumnSelector(
    fileFormat: 'xlsx' | 'csv' | 'xls',
    searchParams: any,
    typeOrders: string,
    userRole?: string
  ): Promise<void> {
    this.alertService.loading('swal_messages.please_wait');

    this.ordersService.getExportColumns('all')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (response) => {
          this.alertService.close();
          const columns: { key: string; label: string }[] = response.data || [];
          await this.openColumnSelectorDialog(columns, fileFormat, searchParams, typeOrders, userRole);
        },
        error: (error) => {
          this.alertService.error(error.error);
        }
      });
  }

  private async openColumnSelectorDialog(
    columns: { key: string; label: string }[],
    fileFormat: 'xlsx' | 'csv' | 'xls',
    searchParams: any,
    typeOrders: string,
    userRole?: string
  ): Promise<void> {
    const selectColumnsTitle = await firstValueFrom(this.translate.get("swal_messages.export.select_columns_title"));
    const selectAllText = await firstValueFrom(this.translate.get("swal_messages.export.select_all"));
    const deselectAllText = await firstValueFrom(this.translate.get("swal_messages.export.deselect_all"));
    const exportButtonText = await firstValueFrom(this.translate.get("swal_messages.export.export_button"));
    const noColumnsText = await firstValueFrom(this.translate.get("swal_messages.export.no_columns_selected"));

    const columnsPerRow = 3;
    let checkboxesHtml = columns.map(col => `
      <div style="flex: 0 0 calc(33.33% - 8px); min-width: 200px; padding: 4px 0;">
        <label style="display: flex; align-items: center; cursor: pointer; font-size: 14px; gap: 6px;">
          <input type="checkbox" class="column-checkbox" value="${col.key}" checked style="width: 16px; height: 16px; cursor: pointer;">
          <span>${col.label}</span>
        </label>
      </div>
    `).join('');

    const html = `
      <div style="text-align: left; max-height: 60vh; overflow-y: auto; padding: 8px;">
        <div style="margin-bottom: 12px; display: flex; gap: 8px;">
          <button type="button" id="selectAllBtn" class="swal2-styled" style="padding: 6px 16px; font-size: 13px; background-color: #6c757d; border-radius: 6px;">
            ${deselectAllText}
          </button>
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 4px 8px;">
          ${checkboxesHtml}
        </div>
      </div>
    `;

    const result = await Swal.fire({
      title: selectColumnsTitle,
      html: html,
      width: '800px',
      showCancelButton: true,
      confirmButtonText: exportButtonText,
      cancelButtonText: await firstValueFrom(this.translate.get("buttons.cancel")),
      didOpen: () => {
        const selectAllBtn = document.getElementById('selectAllBtn');
        let allSelected = true;

        if (selectAllBtn) {
          selectAllBtn.addEventListener('click', () => {
            const checkboxes = document.querySelectorAll('.column-checkbox') as NodeListOf<HTMLInputElement>;
            allSelected = !allSelected;
            checkboxes.forEach(cb => cb.checked = allSelected);
            selectAllBtn.textContent = allSelected ? deselectAllText : selectAllText;
          });
        }
      },
      preConfirm: () => {
        const checkboxes = document.querySelectorAll('.column-checkbox:checked') as NodeListOf<HTMLInputElement>;
        const selected = Array.from(checkboxes).map(cb => cb.value);
        if (selected.length === 0) {
          Swal.showValidationMessage(noColumnsText);
          return false;
        }
        return selected;
      }
    });

    if (result.isConfirmed && result.value) {
      const selectedColumns: string[] = result.value;
      this.exportOrdersWithColumns(fileFormat, searchParams, typeOrders, selectedColumns, userRole);
    }
  }

  private exportOrdersWithColumns(
    type: 'xlsx' | 'csv' | 'xls',
    searchParams: any,
    typeOrders: string,
    columns: string[],
    userRole?: string
  ): void {
    this.alertService.loading('swal_messages.please_wait');

    const searchParamsTemp = { ...searchParams };
    searchParamsTemp['format'] = type;
    searchParamsTemp['header_type'] = 'all';
    searchParamsTemp['columns[]'] = columns;

    if (typeOrders !== 'telemarketing-pending') {
      searchParamsTemp['stage_type'] = typeOrders || 'all';
    }

    if (userRole === 'Telemarketing' || typeOrders === 'telemarketing-pending' || typeOrders === 'telemarketing-done') {
      searchParamsTemp['telemarketing'] = 1;
      searchParamsTemp['telemarketing_action_type'] = typeOrders;
    }

    this.ordersService
      .exportData(typeOrders, searchParamsTemp)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          let url = data.data.url;
          const a = document.createElement('a');
          a.href = url;
          a.download = `orders_${typeOrders}_custom_${new Date().toISOString().split('T')[0]}.${type}`;
          a.click();
          this.alertService.close();
        },
        error: (error) => {
          this.alertService.error(error.error);
        },
      });
  }

  exportOrders(
    type: 'xlsx' | 'csv' | 'xls',
    format: string,
    searchParams: any,
    typeOrders: string,
    userRole?: string
  ): void {
    this.alertService.loading('swal_messages.please_wait');

    const searchParamsTemp = { ...searchParams };
    searchParamsTemp['format'] = type;
    searchParamsTemp['header_type'] = format;

    if (typeOrders !== 'telemarketing-pending') {
      searchParamsTemp['stage_type'] = typeOrders || 'all';
    }

    if (userRole === 'Telemarketing' || typeOrders === 'telemarketing-pending' || typeOrders === 'telemarketing-done') {
      searchParamsTemp['telemarketing'] = 1;
      searchParamsTemp['telemarketing_action_type'] = typeOrders;
    }

    this.ordersService
      .exportData(typeOrders, searchParamsTemp)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          let url = data.data.url;
          const a = document.createElement('a');
          a.href = url;
          a.download = `orders_${typeOrders || 'all'}_${new Date().toISOString().split('T')[0]}.${type}`;
          a.click();
          this.alertService.close();
        },
        error: (error) => {
          this.alertService.error(error.error);
        },
      });
  }

  exportToExcel(searchParams: any, typeOrders: string, userRole?: string): void {
    this.exportOrders('xlsx', 'all', searchParams, typeOrders, userRole);
  }

  exportToCSV(searchParams: any, typeOrders: string, userRole?: string): void {
    this.exportOrders('csv', 'all', searchParams, typeOrders, userRole);
  }

  exportToXLS(searchParams: any, typeOrders: string, userRole?: string): void {
    this.exportOrders('xls', 'all', searchParams, typeOrders, userRole);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
